import { type Dispatch, type ReactElement, type SetStateAction, type TextareaHTMLAttributes } from 'react';
import { type SmsAuthType, type SmsFormField, type SmsFormIssue, type SmsIntegrationForm, type WhatsappFormIssue } from '../../src/index';
import { useT } from '../i18n/provider';
import { Field, Select, TextInput, FormGrid, Row, Stack, MonoArea } from '@vello/web-theme';

/**
 * Shared OTP slot form pieces (D320 → D343). The classic *Integrações › SMS* panel and the guided setup wizard render
 * the SAME SMS gateway fields over the same view-model (`normalizeSmsForm`), so the two entry points can never drift
 * on the real slot schema (endpoint, method, encoding, auth, headers, body map, sender, per-locale text, message-id
 * path, cost, write-only key).
 */

export const OTP_ISSUE_KEY: Readonly<Record<WhatsappFormIssue['code'] | SmsFormIssue['code'], string>> = {
  REQUIRED: 'int.err.required',
  INVALID_VALUE: 'int.err.invalid',
  INVALID_JSON: 'int.err.json',
  INVALID_TEMPLATES: 'int.err.templates',
  INVALID_DEFAULT_LOCALE: 'int.err.defaultLocale',
  MISSING_PLACEHOLDER: 'int.err.placeholder',
  INVALID_COST: 'int.err.cost',
};

export const SMS_AUTH_TYPES: readonly SmsAuthType[] = ['bearer', 'basic', 'header', 'query', 'body'];

/** A monospace multi-line control for the JSON maps (templates, body, messages) — same tokens as `TextInput`. */
export function JsonArea({ compact, ...props }: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'style' | 'className'> & { compact?: boolean }): ReactElement {
  return <MonoArea {...props} {...(compact === true ? { compact } : {})} />;
}

/**
 * The SMS gateway fields (no `enabled` control — the caller decides whether this surface may flip it: the classic
 * panel does, the wizard never does outside its review step).
 */
export function SmsGatewayFields({
  idPrefix,
  form,
  setForm,
  errors,
  configured,
  maskedHint,
}: {
  idPrefix: string;
  form: SmsIntegrationForm;
  setForm: Dispatch<SetStateAction<SmsIntegrationForm>>;
  errors: Partial<Record<SmsFormField, string>>;
  configured: boolean;
  maskedHint: string | undefined;
}): ReactElement {
  const { t } = useT();
  const set = (field: keyof SmsIntegrationForm) => (v: string | boolean): void => setForm((f) => ({ ...f, [field]: v }));
  const authNeedsName = form.authType !== 'bearer';
  const id = (name: string): string => `${idPrefix}-${name}`;
  return (
    <>
      <FormGrid>
        <Field label={t('int.sms.field.endpoint')} htmlFor={id('endpoint')} hint={errors.endpoint}>
          <TextInput id={id('endpoint')} type="url" value={form.endpoint} onChange={(e) => set('endpoint')(e.target.value)} placeholder="https://…" required />
        </Field>
        <Field label={t('int.sms.field.method')} htmlFor={id('method')}>
          <Select id={id('method')} value={form.method} onChange={(e) => set('method')(e.target.value === 'PUT' ? 'PUT' : 'POST')}>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
          </Select>
        </Field>
        <Field label={t('int.sms.field.encoding')} htmlFor={id('encoding')}>
          <Select id={id('encoding')} value={form.encoding} onChange={(e) => set('encoding')(e.target.value === 'form' ? 'form' : 'json')}>
            <option value="json">JSON</option>
            <option value="form">application/x-www-form-urlencoded</option>
          </Select>
        </Field>
        <Field label={t('int.sms.field.authType')} htmlFor={id('auth-type')} hint={errors.auth}>
          <Select id={id('auth-type')} value={form.authType} onChange={(e) => setForm((f) => ({ ...f, authType: (SMS_AUTH_TYPES as readonly string[]).includes(e.target.value) ? (e.target.value as SmsAuthType) : 'bearer' }))}>
            {SMS_AUTH_TYPES.map((a) => (
              <option key={a} value={a}>{t(`int.sms.auth.${a}`)}</option>
            ))}
          </Select>
        </Field>
        {authNeedsName && (
          <Field label={t('int.sms.field.authName')} htmlFor={id('auth-name')} hint={errors.auth}>
            <TextInput id={id('auth-name')} value={form.authName} onChange={(e) => set('authName')(e.target.value)} />
          </Field>
        )}
        {form.authType === 'header' && (
          <Field label={t('int.sms.field.authPrefix')} htmlFor={id('auth-prefix')}>
            <TextInput id={id('auth-prefix')} value={form.authPrefix} onChange={(e) => set('authPrefix')(e.target.value)} />
          </Field>
        )}
        <Field label={t('int.sms.field.from')} htmlFor={id('from')}>
          <TextInput id={id('from')} value={form.from} onChange={(e) => set('from')(e.target.value)} placeholder="VELLO" />
        </Field>
        <Field label={t('int.otp.field.defaultLocale')} htmlFor={id('default-locale')} hint={errors.defaultLocale ?? t('int.otp.field.defaultLocale.hint')}>
          <TextInput id={id('default-locale')} value={form.defaultLocale} onChange={(e) => set('defaultLocale')(e.target.value)} placeholder="pt" />
        </Field>
        <Field label={t('int.sms.field.messageIdPath')} htmlFor={id('message-id-path')} hint={errors.messageIdPath}>
          <TextInput id={id('message-id-path')} value={form.messageIdPath} onChange={(e) => set('messageIdPath')(e.target.value)} placeholder="data.id" />
        </Field>
        <Field label={t('int.sms.field.successPath')} htmlFor={id('success-path')} hint={errors.successCheck ?? t('int.sms.field.successPath.hint')}>
          <TextInput id={id('success-path')} value={form.successPath} onChange={(e) => set('successPath')(e.target.value)} placeholder="data.0.status" />
        </Field>
        <Field label={t('int.sms.field.successValues')} htmlFor={id('success-values')} hint={errors.successCheck}>
          <TextInput id={id('success-values')} value={form.successValues} onChange={(e) => set('successValues')(e.target.value)} placeholder="OK" />
        </Field>
        <Field label={t('int.sms.field.errorCodePath')} htmlFor={id('error-code-path')} hint={errors.errorCodePath}>
          <TextInput id={id('error-code-path')} value={form.errorCodePath} onChange={(e) => set('errorCodePath')(e.target.value)} placeholder="error" />
        </Field>
        <Field label={t('int.otp.field.cost')} htmlFor={id('cost-minor')} hint={errors.approxCost ?? t('int.otp.field.cost.hint')}>
          <Row gap={2} align="stretch">
            <TextInput id={id('cost-minor')} inputMode="numeric" value={form.approxCostMinor} onChange={(e) => set('approxCostMinor')(e.target.value)} placeholder="9" />
            <TextInput id={id('cost-currency')} aria-label={t('int.otp.field.cost.currency')} value={form.approxCostCurrency} onChange={(e) => set('approxCostCurrency')(e.target.value)} placeholder="USD" className="vello-admin-a-narrow" />
          </Row>
        </Field>
        <Field label={t('int.sms.field.apiKey')} htmlFor={id('api-key')} hint={configured ? t('int.field.apiKey.hintConfigured', { hint: maskedHint ?? '' }) : t('int.field.apiKey.hint')}>
          <TextInput id={id('api-key')} type="password" autoComplete="off" value={form.apiKey} onChange={(e) => set('apiKey')(e.target.value)} placeholder={configured ? '••••••••' : ''} />
        </Field>
      </FormGrid>
      <Stack gap={3}>
        <Field label={t('int.sms.field.body')} htmlFor={id('body')} hint={errors.bodyJson ?? t('int.sms.field.body.hint')}>
          <JsonArea id={id('body')} value={form.bodyJson} onChange={(e) => set('bodyJson')(e.target.value)} />
        </Field>
        <Field label={t('int.sms.field.messages')} htmlFor={id('messages')} hint={errors.messagesJson ?? t('int.sms.field.messages.hint')}>
          <JsonArea id={id('messages')} value={form.messagesJson} onChange={(e) => set('messagesJson')(e.target.value)} />
        </Field>
        <Field label={t('int.sms.field.headers')} htmlFor={id('headers')} hint={errors.headersJson}>
          <JsonArea id={id('headers')} value={form.headersJson} onChange={(e) => set('headersJson')(e.target.value)} compact />
        </Field>
      </Stack>
    </>
  );
}
